/**
 * Global overlay stacking (higher = visually on top).
 * Child overlays must use a tier above the surface that opened them.
 * Keep in sync with portaled modals (e.g. ManualInvoiceBuilderModal → document.body).
 */
export const overlayLayers = {
  /**
   * Desktop nav rail (collapsed tooltips extend into the main column).
   * Must sit above main content (including sticky sub-tabs) but below overlays/dropdowns.
   */
  navRail: 105,
  stickyHeader: 100,
  /** Mobile bottom tab chrome + blur vignette (portaled to body). */
  bottomTabChrome: 100,
  dropdown: 120,
  /** Form drawer scrim (customer edit, assign, etc.) — above nav rail */
  formDrawerBackdrop: 130,
  /** Form drawer panel — above its own scrim */
  formDrawerPanel: 131,
  navDrawerBackdrop: 140,
  navDrawer: 150,
  /**
   * Owner setup guide (SetupGuideShell) — a full-screen takeover mounted from
   * AdminHome, modelled on the MFA gate's approach but edge-to-edge (not a
   * bounded modal). Sits above app nav/content chrome (navRail, stickyHeader,
   * bottomTabChrome, nav drawer) — it replaces the whole viewport while open —
   * but BELOW every normal modal tier (modal, adminModal, invoicePreview,
   * confirmDialog…) so a nested overlay opened FROM WITHIN the guide
   * (CustomerImportModal, the invoice preview "See the real PDF" link, any
   * ConfirmDialog) stacks correctly above it with no special-case zIndex. Also
   * below mfaEnrollmentGate and appUpdateGate, which can supersede everything.
   */
  setupGuide: 170,
  /**
   * KAN-1028 — the bulk-run background pill. Above the nav rail, bottom tab bar
   * and bulk action bars (<= 150), below every modal tier so an open dialog
   * covers it.
   */
  bulkRunPill: 175,
  /**
   * Generic centered modal / bottom sheet portaled to body — above page chrome
   * (sticky header, sticky bottom bars, in-context drawers). Used by route
   * override modals, LineItemPicker, etc. Below admin-specific overlays.
   */
  modal: 180,
  adminModal: 200,
  /**
   * A modal opened FROM WITHIN another modal that already sits at `adminModal`
   * (e.g. "Poner precio" / PriceExtraLineModal launched from inside the
   * lifecycle transition sheet, PeriodSummaryModal, both admin surfaces).
   * Without an explicit tier above its parent, a nested modal defaults to
   * `overlayLayers.modal` (180) — BELOW its own parent's 200 — and paints
   * behind it instead of on top.
   */
  nestedModal: 210,
  /**
   * KAN-1028 — a bulk run's progress modal (running / complete), opened from the
   * pill. Must land ABOVE the builder scrim/dialog (10000) but BELOW the builder-tier
   * overlays (popovers 10010, drawers 10020+, date picker 10030, confirms 10040) so
   * a confirm opened while it is visible still renders on top of it, and under the
   * MFA gate and toasts. Its review phase keeps `modal`. A confirm that can open over
   * the progress modal uses `confirmDialogAboveManualInvoice` (10040).
   */
  bulkRunProgressModal: 10005,
  invoicePreview: 300,
  /**
   * KAN-1178 "Peek": an invoice preview opened FROM an estimate preview while the
   * estimate stays mounted underneath (both previews are 300, so the later DOM
   * node would win by luck). Only used while an estimate preview is open. Still
   * below invoicePreviewInlinePopover (310), confirmDialog (400) and the Send
   * screen (10040), so everything the invoice preview opens stays on top.
   */
  invoicePreviewOverDocument: 305,
  /** FilterSelect popover opened from inside the invoice-preview modal (e.g. the
   *  sidebar "Add job" dropdown). Above the preview (300) so its options are
   *  clickable, below confirmDialog (400) so confirms still sit on top. Without
   *  this the dropdown defaults to z=200 and renders BEHIND the modal. Mirrors
   *  manualInvoiceInlinePopover (required on BOTH desktop and mobile now that
   *  FilterSelect is always the branded listbox). */
  invoicePreviewInlinePopover: 310,
  confirmDialog: 400,
  /** Confirm dialog that opens ON TOP of another modal (e.g. season override → confirm) */
  confirmDialogAbove: 450,
  /** Logout confirmation sheet (portaled bottom sheet above nav drawer) */
  logoutSheetBackdrop: 460,
  logoutSheetPanel: 461,
  /** Manual invoice builder (full-screen backdrop + dialog; portaled to body) */
  manualInvoiceModal: 10000,
  /** Floating AI button (desktop / bar-less fallback). BELOW aiAssistantPanel so the panel always covers it. */
  aiFab: 10000,
  /** Same tier as manual invoice scrim (e.g. mobile “desktop required” gate) */
  manualInvoiceScrim: 10000,
  /** Admin slide-over backdrop when opened above manual invoice modal */
  drawerAboveManualInvoiceBackdrop: 10020,
  /** Admin slide-over panel */
  drawerAboveManualInvoicePanel: 10021,
  /** Date picker when portaled to document.body (above manual invoice modal) */
  datePickerFixedToBody: 10030,
  /** FilterSelect popover when opened from inside the manual invoice modal */
  manualInvoiceInlinePopover: 10010,
  /** Confirm dialog opened from inside the manual invoice modal (RD-SEC-005
   *  backdate guard). Higher than the modal itself so the dialog isn't
   *  hidden behind its parent scrim. */
  confirmDialogAboveManualInvoice: 10040,
  /**
   * Forced MFA enrollment interstitial (TenantMfaEnrollmentGate /
   * PlatformMfaEnrollmentGate). Rendered by ProtectedRoute/PlatformRoute INSTEAD
   * of the app, portaled to body via AnimatedModalShell. Non-dismissible. Sits
   * above in-app overlays but below the toast it never needs and below the
   * app-update gate (which can supersede everything).
   */
  /**
   * Recovery codes shown during forced MFA enrollment (TenantMfaEnrollmentGate /
   * PlatformMfaEnrollmentGate). Must sit ABOVE mfaEnrollmentGate (11000) so the
   * one-time codes modal is visible and dismissible — otherwise the gate never
   * clears and the user is stuck on "Sign out" only.
   */
  mfaRecoveryCodes: 11100,
  mfaEnrollmentGate: 11000,
  /** Portaled toast (above drawers/modals; below future critical overlays) */
  globalToast: 12100,
  /**
   * AI assistant floating panel (portaled to document.body).
   * Below global toast; above in-app modals/drawers so the scrim blocks the shell.
   */
  aiAssistantBackdrop: 11800,
  aiAssistantPanel: 11801,
  /** Floating keyboard-dismiss pill (mobile only, above AI panel, below toast). */
  keyboardDismiss: 12000,
  /**
   * Mandatory app-update gate (UpdateAvailableBanner). Must sit ABOVE every
   * other overlay — modals, the manual-invoice tier (10000+), AI panel, toasts —
   * because it's a non-dismissible "you must update" block. Highest layer in the app.
   */
  appUpdateGate: 12500,
};
